'use client'

import { formatDistanceToNowStrict } from 'date-fns'
import { Loader2Icon, Trash2Icon } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import { SettingsCard, SettingsCardHeader } from '@/components/dialog/settings/settings-card'
import {
  SETTINGS_ITEM_ATTR,
  SETTINGS_LIST_CLASS,
  SETTINGS_LIST_ITEM_CLASS,
  SETTINGS_PANEL_CLASS,
} from '@/components/dialog/settings/settings-card-styles'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/features/auth/hooks'
import { toast } from '@/lib/toast'
import { getDateFnsLocale } from '@/lib/utils/date-locale'
import {
  useListSessions,
  useLogout,
  useRevokeAllOtherSessions,
  useRevokeSession,
} from '@/service/session.hook'
import { SessionInfo } from '@/types/generated/common/auth'

export default function DevicesPanel() {
  const t = useTranslations('settings')
  const { logout } = useAuth()
  const { data, isLoading, isError } = useListSessions()
  const [revokeTarget, setRevokeTarget] = useState<SessionInfo | null>(null)
  const [isRevokeAllOpen, setIsRevokeAllOpen] = useState(false)

  // React Query v5 has no per-query onError — surface load failures from state.
  useEffect(() => {
    if (isError) toast.error(t('devices.loadFailed'))
  }, [isError, t])

  const sessions = data?.sessions ?? []

  const { mutateAsync: revokeSession, isPending: isRevoking } = useRevokeSession({
    onSuccess: () => {
      toast.success(t('devices.revokedSuccess'))
      setRevokeTarget(null)
    },
    onError: (error) => {
      toast.error(error.message || t('devices.revokeFailed'))
      setRevokeTarget(null)
    },
  })

  const { mutateAsync: revokeAllOtherSessions, isPending: isRevokingAll } =
    useRevokeAllOtherSessions({
      onSuccess: () => {
        toast.success(t('devices.revokedAllSuccess'))
        setIsRevokeAllOpen(false)
      },
      onError: (error) => {
        toast.error(error.message || t('devices.revokeFailed'))
        setIsRevokeAllOpen(false)
      },
    })

  const { mutateAsync: logoutSession, isPending: isLoggingOut } = useLogout()

  const handleLogout = async () => {
    // Best-effort server-side revocation of this device's session row; the
    // existing logout() flow still clears the httpOnly refresh cookie via
    // DELETE /api/auth/session even if this RPC fails.
    try {
      await logoutSession()
    } catch {
      // ignore — local logout must proceed regardless
    }
    logout()
  }

  return (
    <div className={SETTINGS_PANEL_CLASS}>
      <SettingsCard>
        <div className="flex w-full items-center justify-between gap-4">
          <SettingsCardHeader title={t('devices.title')} description={t('devices.description')} />
          <Button
            variant="secondary"
            size="sm"
            disabled={sessions.length <= 1 || isRevokingAll}
            onClick={() => setIsRevokeAllOpen(true)}
          >
            {isRevokingAll ? (
              <Loader2Icon className="size-3.5 animate-spin" />
            ) : (
              t('devices.revokeAll')
            )}
          </Button>
        </div>
        {isLoading ? (
          <SessionListSkeleton />
        ) : isError ? (
          <p className="text-foreground/60 text-sm tracking-tight">{t('devices.loadFailed')}</p>
        ) : sessions.length === 0 ? (
          <p className="text-foreground/60 text-sm tracking-tight">{t('devices.noSessions')}</p>
        ) : (
          <div className={SETTINGS_LIST_CLASS}>
            {sessions.map((session) => (
              <SessionRow
                key={session.sessionId}
                session={session}
                isRevoking={isRevoking}
                onRevoke={() => setRevokeTarget(session)}
              />
            ))}
          </div>
        )}
      </SettingsCard>

      <SettingsCard layout="row">
        <SettingsCardHeader
          title={t('devices.logOutTitle')}
          description={t('devices.logOutDescription')}
        />
        <Button variant="secondary" size="sm" disabled={isLoggingOut} onClick={handleLogout}>
          {isLoggingOut ? <Loader2Icon className="size-3.5 animate-spin" /> : t('devices.logOut')}
        </Button>
      </SettingsCard>

      {/* Per-session revoke confirmation */}
      <AlertDialog
        open={Boolean(revokeTarget)}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('devices.revokeSessionTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('devices.revokeSessionDescription', {
                device: getDeviceLabel(revokeTarget?.deviceLabel, t('devices.unknownDevice')),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRevoking}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={isRevoking}
              onClick={async () => {
                if (!revokeTarget) return
                await revokeSession({ sessionId: revokeTarget.sessionId })
              }}
            >
              {isRevoking ? t('devices.revoking') : t('devices.revoke')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke-all confirmation */}
      <AlertDialog open={isRevokeAllOpen} onOpenChange={setIsRevokeAllOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('devices.revokeAllTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('devices.revokeAllDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRevokingAll}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={isRevokingAll}
              onClick={async () => {
                await revokeAllOtherSessions()
              }}
            >
              {isRevokingAll ? t('devices.revoking') : t('devices.revokeAll')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

function formatLocation(session: SessionInfo): string | null {
  const parts = [session.geoCity, session.geoCountry].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
}

function getDeviceLabel(deviceLabel: string | undefined, unknownDeviceLabel: string): string {
  return deviceLabel && deviceLabel.toLowerCase() !== 'unknown device'
    ? deviceLabel
    : unknownDeviceLabel
}

const SessionRow = ({
  session,
  isRevoking,
  onRevoke,
}: {
  session: SessionInfo
  isRevoking: boolean
  onRevoke: () => void
}) => {
  const t = useTranslations('settings')
  const locale = useLocale()
  const location = formatLocation(session)
  const deviceLabel = getDeviceLabel(session.deviceLabel, t('devices.unknownDevice'))

  return (
    <div {...SETTINGS_ITEM_ATTR} className={SETTINGS_LIST_ITEM_CLASS}>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <p className="text-foreground text-sm tracking-tight">{deviceLabel}</p>
          {session.isCurrent && (
            <Badge variant="outline" className="text-foreground/60 text-xs">
              {t('devices.currentSession')}
            </Badge>
          )}
        </div>
        <p className="text-foreground/60 text-xs tracking-tight">
          {/* Telegram-linked sessions are created by a webhook delivery, not a
              real client connection — ip/geo are empty by design (see
              TelegramLinkingCard), not a bug to paper over with a blank. */}
          {session.ip || '—'}
          {location ? ` — ${location}` : ''}
          {' — '}
          {t('devices.lastActive', {
            time: formatDistanceToNowStrict(Number(session.lastUsedAt), {
              addSuffix: true,
              locale: getDateFnsLocale(locale),
            }),
          })}
        </p>
      </div>
      {!session.isCurrent && (
        <Button
          aria-label={t('devices.revoke')}
          variant="ghost"
          size="icon"
          className="hover:bg-foreground/5 bg-transparent"
          disabled={isRevoking}
          onClick={onRevoke}
        >
          <Trash2Icon className="text-destructive size-4" />
        </Button>
      )}
    </div>
  )
}

const SessionListSkeleton = () => (
  <div className={SETTINGS_LIST_CLASS}>
    {Array.from({ length: 2 }).map((_, index) => (
      <div key={index} className={SETTINGS_LIST_ITEM_CLASS}>
        <div className="flex flex-col gap-2">
          <Skeleton className="bg-foreground/10 h-4 w-36" />
          <Skeleton className="bg-foreground/10 h-3 w-28" />
        </div>
        <Skeleton className="bg-foreground/10 size-8 rounded-full" />
      </div>
    ))}
  </div>
)
